/**
 * Phase J.3.2 — friendly labels + per-event icon mapping shared by the
 * bell, list pages, and preferences pages. Single source of truth so we
 * don't have label drift between surfaces.
 */
import {
    Mail, Bell, AlertCircle, CheckCircle2, Clock, Undo2,
    FileSignature, FileCheck, ShieldAlert, Eye,
} from "lucide-react";

export const EVENT_META = {
    invoice_issued: {
        label: "Invoice issued",
        description: "A new invoice has been sent to you.",
        icon: Mail,
        accent: "text-blue-700 bg-blue-50 ring-blue-200",
        audience: "customer",
    },
    invoice_reminder: {
        label: "Invoice reminder",
        description: "An invoice is due soon, or now overdue.",
        icon: Clock,
        accent: "text-amber-800 bg-amber-50 ring-amber-200",
        audience: "customer",
    },
    payment_processing: {
        label: "Payment processing",
        description: "Your ACH payment is processing (3–5 business days).",
        icon: Clock,
        accent: "text-amber-800 bg-amber-50 ring-amber-200",
        audience: "customer",
    },
    payment_succeeded: {
        label: "Payment received",
        description: "Your ACH payment has cleared.",
        icon: CheckCircle2,
        accent: "text-emerald-800 bg-emerald-50 ring-emerald-200",
        audience: "customer",
    },
    payment_failed: {
        label: "Payment failed",
        description: "There was a problem completing your payment.",
        icon: AlertCircle,
        accent: "text-rose-800 bg-rose-50 ring-rose-200",
        audience: "customer",
    },
    refund_issued: {
        label: "Refund issued",
        description: "A refund has been issued on one of your invoices.",
        icon: Undo2,
        accent: "text-purple-800 bg-purple-50 ring-purple-200",
        audience: "customer",
    },
    contract_sent: {
        label: "Contract sent",
        description: "A contract is waiting for your signature.",
        icon: FileSignature,
        accent: "text-blue-700 bg-blue-50 ring-blue-200",
        audience: "customer",
    },
    contract_countersigned: {
        label: "Contract executed",
        description: "Your contract has been countersigned and is fully executed.",
        icon: FileCheck,
        accent: "text-emerald-800 bg-emerald-50 ring-emerald-200",
        audience: "customer",
    },
    coi_expiring: {
        label: "COI expiring soon",
        description: "Certificate of insurance is approaching expiration (30 / 14 / 7 day thresholds).",
        icon: ShieldAlert,
        accent: "text-amber-800 bg-amber-50 ring-amber-200",
        audience: "both",
    },
    // Phase COI-REFRESH — daily cron fires these when a rental's COI is
    // approaching expiry or has lapsed past the grace period. Admins
    // dismiss the card by opening it (marks as read); customers get a
    // separate branded email + bell.
    coi_refresh_pending: {
        label: "COI refresh pending",
        description: "A fresh COI request was auto-sent to the insurance agent on file.",
        icon: ShieldAlert,
        accent: "text-blue-700 bg-blue-50 ring-blue-200",
        audience: "admin",
    },
    coi_lapse_warning: {
        label: "COI lapsed",
        description: "The COI on file has expired and no replacement has been received.",
        icon: ShieldAlert,
        accent: "text-rose-800 bg-rose-50 ring-rose-200",
        audience: "admin",
    },
    coi_customer_lapse: {
        label: "Insurance coverage update needed",
        description: "Your certificate of insurance has expired — please forward a fresh copy.",
        icon: ShieldAlert,
        accent: "text-rose-800 bg-rose-50 ring-rose-200",
        audience: "customer",
    },
    customer_viewed_invoice: {
        label: "Customer viewed invoice",
        description: "A customer opened one of their invoices for the first time.",
        icon: Eye,
        accent: "text-slate-700 bg-slate-100 ring-slate-200",
        audience: "admin",
    },
    recurring_invoice_issued: {
        label: "Recurring invoice issued",
        description: "An auto-generated invoice from a recurring billing schedule.",
        icon: Mail,
        accent: "text-blue-700 bg-blue-50 ring-blue-200",
        audience: "admin",
    },
};

export function getEventMeta(eventType) {
    return EVENT_META[eventType] || {
        label: eventType,
        description: "",
        icon: Bell,
        accent: "text-slate-700 bg-slate-100 ring-slate-200",
        audience: "both",
    };
}

/** Filter the catalog to the events relevant for a given role. */
export function catalogForRole(role) {
    return Object.keys(EVENT_META).filter((k) => {
        const a = EVENT_META[k].audience;
        if (role === "customer") return a === "customer" || a === "both";
        return a === "admin" || a === "both" || a === "customer"; // admins see every key on their prefs page so they can mute customer-targeted noise that bubbles via shared inboxes
    });
}

/** Filter the catalog to the events that ACTUALLY get delivered to a role.
 *  For now this is identical to {@link catalogForRole} but we expose it
 *  separately so the prefs UI can surface only events that will produce
 *  notifications for the current role (when the catalogues diverge later
 *  this is the single switch). */
export function deliverableForRole(role) {
    return Object.keys(EVENT_META).filter((k) => {
        const a = EVENT_META[k].audience;
        if (role === "customer") return a === "customer" || a === "both";
        return a === "admin" || a === "both";
    });
}

export const DEFAULT_PREFS = {
    invoice_issued:          { bell: true,  email: true  },
    invoice_reminder:        { bell: true,  email: true  },
    payment_processing:      { bell: true,  email: false },
    payment_succeeded:       { bell: true,  email: true  },
    payment_failed:          { bell: true,  email: true  },
    refund_issued:           { bell: true,  email: true  },
    contract_sent:           { bell: true,  email: true  },
    contract_countersigned:  { bell: true,  email: true  },
    coi_expiring:            { bell: true,  email: true  },
    coi_refresh_pending:     { bell: true,  email: false },
    coi_lapse_warning:       { bell: true,  email: false },
    coi_customer_lapse:      { bell: true,  email: true  },
    customer_viewed_invoice: { bell: true,  email: false },
    recurring_invoice_issued: { bell: true,  email: false },
};
